import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';

import DropDown from 'src/components/common/field/DropDown';
import InputCustom from 'src/components/common/field/InputCustom';
import IconButtonCustom from 'src/components/quotations/IconButtonCustom';
import {
  ItemTypePayment,
  DataPaymentType,
} from 'src/components/quotations/TypeCustom';
import { useEffect, useState } from 'react';
import { Button } from '@mui/material';

import { getAPI } from 'src/utils/api';
import { getToken } from 'src/utils/auth';
import { getField } from 'src/common/helper';

type RecordProps = {
  items: ItemTypePayment[];
  loading?: boolean;
  dataPayment: DataPaymentType;
  onClickAddRow?: () => void;
  onClickDelete?: (index: number) => void;
  onChangeItem: (
    index: number,
    fieldName: string,
    value: string | number | null
  ) => void;
  error?: any;
  onClickCopyMonitor?: () => void;
  patientId: string;
};

const Record: React.FC<RecordProps> = (props) => {
  const [dataPayment, setDataPayment] = useState<any>();

  const handleClickCoppyMonitor = () => {
    props.onClickCopyMonitor?.();
  };

  const handleChangeDropDown =
    (index: number, fieldName: string) => (value: string | number) => {
      props.onChangeItem(index, fieldName, value);
    };

  const handleChangeInput =
    (index: number, fieldName: string) => (value: string | number | null) => {
      props.onChangeItem(index, fieldName, value);
    };

  const handleDeleteRow = (index: number) => () => {
    props.onClickDelete?.(index);
  };

  const calPriceDiscount = (
    price: number | null,
    discountid: number | null
  ) => {
    let priceCal = price;

    return formatNumber(Number(priceCal));
  };

  const convertItemType = (
    data: any[],
    id: string,
    name: string,
    accessories?: string
  ) => {
    const converted = data
      ? data.map((item: any) => ({
        id: String(item[id]),
        name: item[name],
        accessories:
          accessories && item[accessories] ? item[accessories] : '',
      }))
      : [];

    return converted;
  };

  const findArray = (
    data: any[],
    fieldName: string,
    value: string | number | null
  ) => {
    return data.find((item) => item[fieldName] && Number(item[fieldName]) === Number(value));
  };

  const getDataDropDown = (name: string) => {
    return dataPayment && dataPayment[name] ? dataPayment[name] : [];
  };

  const formatNumber = (number: number): string => {
    return new Intl.NumberFormat('en-US').format(number);
  };

  const checkError = (index: number, fieldName: string) => {
    return (
      props.error &&
      props.error.index === index &&
      props.error.fieldName === fieldName
    );
  };

  const handleClickRemainingAmount = (quoteId: number) => () => {
    try {
      const token = getToken();

      if (!token) {
        throw new Error('Failed to get access token');
      }
      getAPI(
        `/patient/quote-view?patientid=${props.patientId}&id=${quoteId}&token=${token}`
      )
        .then((res: any) => {
          if (res.code === 200 && res.data) {
            handleChangeInput(0, 'amount')(getField(res.data, 'total', 0) - getField(res.data, 'paymenttotal', 0));
          }
        })
    } catch (error) {
      console.error('Failed to fetch data:', error);
      throw error;
    }
  };

  useEffect(() => {
    if (props.dataPayment) {
      setDataPayment({
        categories: convertItemType(
          props.dataPayment.categories,
          'value',
          'label',
          'code'
        ),
        methods: convertItemType(props.dataPayment.methods, 'value', 'label'),
        methods2: convertItemType(props.dataPayment.methods2, 'value', 'label'),
        types: convertItemType(props.dataPayment.types, 'value', 'label'),
        products: convertItemType(props.dataPayment.products, 'id', 'product'),
        quotes: convertItemType(props.dataPayment.quotes, 'id', 'title'),
      });
    }
  }, [props.dataPayment]);

  return (
    <TableContainer
      component={Paper}
      sx={{
        boxShadow: 'none',
        '& *': { color: 'primary.dark', fontSize: '12px' },
      }}
    >
      <Table
        stickyHeader
        sx={{
          width: '100%',
          '& .MuiTableCell-root.MuiTableCell-head': {
            borderBottom: '0px',
            fontWeight: 700,
            fontSize: '12px',
            height: '36px',
            p: '0px 4px',
          },
        }}
        aria-label='simple table'
      >
        <TableHead>
          <TableRow>
            <TableCell
              align={'left'}
              sx={{ width: '10%', bgcolor: 'primary.light' }}
            >
              カテゴリー
            </TableCell>
            <TableCell
              align={'left'}
              sx={{ width: '42%', bgcolor: 'primary.light' }}
            >
              内容
            </TableCell>
            <TableCell
              align={'left'}
              sx={{ width: '11%', bgcolor: 'primary.light' }}
            >
              支払方法
            </TableCell>
            <TableCell
              align={'left'}
              sx={{ width: '10%', bgcolor: 'primary.light' }}
            >
              勘定科目
            </TableCell>
            <TableCell
              align={'left'}
              sx={{ width: '12%', bgcolor: 'primary.light' }}
            >
              金額
            </TableCell>
            <TableCell
              align={'left'}
              sx={{ width: '12%', bgcolor: 'primary.light' }}
            >
              未収金
            </TableCell>
            <TableCell
              align={'left'}
              sx={{ width: '3%', bgcolor: 'primary.light' }}
            ></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {props.loading ? (
            <TableRow>
              <TableCell colSpan={5}>
                <Stack spacing={1}>
                  <Skeleton variant='rounded' width={'100%'} height={40} />
                  <Skeleton variant='rounded' width={'100%'} height={40} />
                  <Skeleton variant='rounded' width={'100%'} height={40} />
                </Stack>
              </TableCell>
            </TableRow>
          ) : (
            props.items.map((item: ItemTypePayment, index: number) => (
              <TableRow
                key={index}
                sx={{
                  '&:last-child td, &:last-child th': { border: 0 },
                  '& .MuiTableCell-root.MuiTableCell-body': {
                    p: '4px',
                    borderBottom: '0px',
                    fontSize: '12px',
                    fontWeight: 400,
                    lineHeight: '14.4px',
                  },
                  height: '48px',
                }}
              >
                <TableCell
                  component='td'
                  scope='row'
                  sx={{ verticalAlign: 'top' }}
                >
                  <Box sx={{ width: '100%' }}>
                    <DropDown
                      idSelect='categoryid'
                      selected={item.categoryid}
                      items={getDataDropDown('categories')}
                      onChange={handleChangeDropDown(index, 'categoryid')}
                      sxInput={{
                        '.MuiSvgIcon-root': { fontSize: '24px' },
                        maxWidth: '120px',
                      }}
                      sxMenuProps={{
                        maxWidth: '120px',
                      }}
                    />
                  </Box>
                </TableCell>

                <TableCell
                  component='td'
                  scope='row'
                  sx={{ verticalAlign: 'top' }}
                >
                  <Stack direction={'row'} spacing={'8px'}>
                    {findArray(
                      getDataDropDown('categories'),
                      'id',
                      item.categoryid
                    )?.accessories === 'product' && (
                        <Stack direction={'row'} spacing={'8px'} width={'70%'}>
                          <Box sx={{ width: '90%' }}>
                            <DropDown
                              idSelect='linkid'
                              selected={item.linkid}
                              items={getDataDropDown('products')}
                              onChange={handleChangeDropDown(index, 'linkid')}
                              sxInput={{
                                '.MuiSvgIcon-root': { fontSize: '24px' },
                                maxWidth: '290px',
                              }}
                              sxMenuProps={{
                                maxWidth: '290px',
                              }}
                              error={checkError(index, 'linkid')}
                            />
                          </Box>
                          <InputCustom
                            name='qty'
                            type='number'
                            min={0}
                            max={99}
                            value={item.qty}
                            onChange={handleChangeInput(index, 'qty')}
                            sx={{ width: '20%' }}
                          />
                        </Stack>
                      )}

                    {findArray(
                      getDataDropDown('categories'),
                      'id',
                      item.categoryid
                    )?.accessories === 'quote' && (
                        <Box sx={{ width: '55%' }}>
                          <DropDown
                            idSelect='linkid'
                            selected={item.linkid}
                            items={getDataDropDown('quotes')}
                            onChange={handleChangeDropDown(index, 'linkid')}
                            sxInput={{
                              '.MuiSvgIcon-root': { fontSize: '24px' },
                              maxWidth: '290px',
                            }}
                            sxMenuProps={{
                              maxWidth: '290px',
                            }}
                            error={checkError(index, 'linkid')}
                          />
                        </Box>
                      )}

                    {findArray(
                      getDataDropDown('categories'),
                      'id',
                      item.categoryid
                    )?.accessories !== 'quote' && (
                        <InputCustom
                          name='detail'
                          type='text'
                          value={item.detail}
                          onChange={handleChangeInput(index, 'detail')}
                          sx={{ width: '30%' }}
                        />
                      )}
                  </Stack>
                </TableCell>

                <TableCell
                  component='td'
                  scope='row'
                  sx={{ verticalAlign: 'top' }}
                >
                  <DropDown
                    idSelect='paymentmethod'
                    selected={item.paymentmethod}
                    items={getDataDropDown('methods')}
                    onChange={handleChangeDropDown(index, 'paymentmethod')}
                    sxInput={{
                      '.MuiSvgIcon-root': { fontSize: '24px' },
                      maxWidth: '132px',
                    }}
                    sxMenuProps={{
                      maxWidth: '132px',
                    }}
                    error={checkError(index, 'paymentmethod')}
                  />
                  {Number(item.paymentmethod) === 2 && (
                    <DropDown
                      idSelect='paymentmethod2'
                      selected={item.paymentmethod2}
                      items={getDataDropDown('methods2')}
                      onChange={handleChangeDropDown(index, 'paymentmethod2')}
                      sx={{ mt: '8px' }}
                      sxInput={{
                        '.MuiSvgIcon-root': { fontSize: '24px' },
                        maxWidth: '132px',
                      }}
                      sxMenuProps={{
                        maxWidth: '132px',
                      }}
                      error={checkError(index, 'paymentmethod2')}
                    />
                  )}
                </TableCell>

                <TableCell
                  component='td'
                  scope='row'
                  sx={{ verticalAlign: 'top' }}
                >
                  <DropDown
                    idSelect='paymenttype'
                    selected={item.paymenttype}
                    items={getDataDropDown('types')}
                    onChange={handleChangeDropDown(index, 'paymenttype')}
                    sxInput={{
                      '.MuiSvgIcon-root': { fontSize: '24px' },
                      maxWidth: '120px',
                    }}
                    sxMenuProps={{
                      maxWidth: '120px',
                    }}
                    error={checkError(index, 'paymenttype')}
                  />
                </TableCell>

                <TableCell
                  component='td'
                  scope='row'
                  sx={{ verticalAlign: 'top' }}
                >
                  <InputCustom
                    name='amount'
                    type='number'
                    value={item.amount}
                    onChange={handleChangeInput(index, 'amount')}
                    sx={{ width: '100%' }}
                  />
                </TableCell>

                <TableCell
                  component='td'
                  scope='row'
                  sx={{ verticalAlign: 'top' }}
                >
                  <InputCustom
                    name='discountPrice'
                    type='text'
                    readonly={true}
                    value={calPriceDiscount(item.amount, null)}
                    onChange={() => { }}
                    sx={{ width: '100%', bgcolor: '#EEEEEE' }}
                    sxInput={{ bgcolor: '#EEEEEE' }}
                  />
                </TableCell>

                <TableCell
                  component='td'
                  scope='row'
                  sx={{ verticalAlign: 'top' }}
                >
                  {item.linkid && (
                  <Button
                    variant='contained'
                    onClick={handleClickRemainingAmount(item.linkid)}
                    >残金を登録</Button>
                  )}
                  {props.items.length > 1 && (
                    <Box
                      sx={{
                        height: '48px',
                        display: 'flex',
                        alignContent: 'center',
                      }}
                    >
                      <IconButton
                        title='Delete row'
                        sx={{ p: 0 }}
                        onClick={handleDeleteRow(index)}
                      >
                        <img
                          src='/images/icons/ic-delete-bin-red.svg'
                          style={{ width: '20px', height: '20px' }}
                          alt='icon'
                        />
                      </IconButton>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      <Stack direction={'row'}>
        <IconButtonCustom
          text='追加する'
          src='/images/ic-plus-blue.svg'
          tooltip='Add row'
          onClick={props.onClickAddRow}
        />
        <Box sx={{ ml: '20px' }}>
          <IconButtonCustom
            text='見積から物販をコピーする'
            src='/images/icons/ic-paper-money-blue.svg'
            tooltip='Add monitor'
            onClick={handleClickCoppyMonitor}
          />
        </Box>
      </Stack>
    </TableContainer>
  );
};

export default Record;
